// Editor das ofertas de um produto — usado no detalhe do produto e no sheet de
// edição. Uma "oferta" é um preço nomeado (Presencial, Online): mesmo produto,
// mesma categoria, mesmos diferenciais, preço diferente.
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Banknote,
  ChevronDown,
  ChevronUp,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { normalizePrice } from "@/lib/price";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function ProductOffersEditor({
  productId,
  canManage,
}: {
  productId: number;
  canManage: boolean;
}) {
  const utils = trpc.useUtils();
  const offersQuery = trpc.crm.productOffers.listByProduct.useQuery({ productId });
  const offers = offersQuery.data ?? [];

  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");

  // Toda mutação mexe no agregado "a partir de" que o card do catálogo, o
  // comparador e o detalhe mostram — por isso invalida os três, não só a lista.
  function invalidateAll() {
    utils.crm.productOffers.listByProduct.invalidate({ productId });
    utils.crm.products.get.invalidate({ id: productId });
    utils.crm.products.list.invalidate();
  }

  const create = trpc.crm.productOffers.create.useMutation({
    onSuccess: () => {
      setNewName("");
      setNewPrice("");
      invalidateAll();
      toast.success("Oferta criada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // `revisao` força o React a remontar os inputs (que usam defaultValue) depois
  // de um update recusado. Sem isso o campo continuava exibindo o valor que o
  // servidor rejeitou — ex.: nome duplicado —, com só um toast dizendo que não
  // colou. A tela passava a mentir sobre o que está salvo.
  const [revisao, setRevisao] = useState(0);

  const update = trpc.crm.productOffers.update.useMutation({
    onSuccess: invalidateAll,
    onError: (e) => {
      toast.error(toUserFriendlyError(e));
      setRevisao((r) => r + 1);
    },
  });

  const reorder = trpc.crm.productOffers.reorder.useMutation({
    onSuccess: invalidateAll,
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const archive = trpc.crm.productOffers.archive.useMutation({
    onSuccess: () => {
      invalidateAll();
      toast.success("Oferta arquivada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // O onError daqui é o ÚNICO: o callback por chamada do useMutation não
  // substitui este, os dois disparam — e o resultado eram dois toasts, um
  // deles sem a ação "Arquivar". O id vem do `variables` que o tRPC repassa.
  const remove = trpc.crm.productOffers.delete.useMutation({
    onSuccess: () => {
      invalidateAll();
      toast.success("Oferta excluída");
    },
    onError: (e, variables) => {
      const msg = toUserFriendlyError(e);
      // O servidor recusa excluir oferta já vendida (histórico). Em vez de só
      // mostrar o erro, oferece a saída correta.
      if (/vinculada a oportunidades/i.test(msg)) {
        toast.error(msg, {
          action: { label: "Arquivar", onClick: () => archive.mutate({ id: variables.id }) },
        });
      } else {
        toast.error(msg);
      }
    },
  });

  function move(index: number, dir: -1 | 1) {
    const next = [...offers];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder.mutate({ productId, orderedIds: next.map((o) => o.id) });
  }

  if (offersQuery.isLoading) {
    return <div className="h-16 bg-muted animate-pulse rounded-md" />;
  }

  return (
    <div className="space-y-2">
      {offers.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Sem ofertas. O preço sugerido do produto vale para todos. Crie ofertas
          quando o mesmo produto é vendido em formatos diferentes — ex.:
          Presencial e Online, cada uma com seu preço.
        </p>
      ) : (
        <div className="space-y-1.5">
          {offers.map((o: any, i: number) => (
            <div
              key={`${o.id}:${revisao}`}
              className="flex items-center gap-1.5 rounded-md border bg-card p-2"
            >
              <div className="flex flex-col shrink-0">
                <button
                  type="button"
                  disabled={!canManage || i === 0}
                  onClick={() => move(i, -1)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-25"
                  title="Subir"
                >
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  disabled={!canManage || i === offers.length - 1}
                  onClick={() => move(i, 1)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-25"
                  title="Descer"
                >
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>

              {canManage ? (
                <>
                  <Input
                    defaultValue={o.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== o.name) update.mutate({ id: o.id, name: v });
                    }}
                    className="h-8 text-sm flex-1 min-w-0"
                  />
                  <div className="relative w-32 shrink-0">
                    <Banknote className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={String(o.price ?? "0")}
                      onBlur={(e) => {
                        // Campo limpo mandaria price:"" — o zod aceitava e o
                        // Postgres estourava com "invalid input syntax for
                        // type numeric". Vazio volta a exibir o valor atual.
                        const v = normalizePrice(e.target.value);
                        if (v === null) {
                          e.target.value = String(o.price ?? "0");
                          return;
                        }
                        if (v !== String(o.price)) update.mutate({ id: o.id, price: v });
                      }}
                      className="h-8 pl-7 text-sm tabular-nums"
                    />
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => remove.mutate({ id: o.id })}
                    title="Excluir"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-sm flex-1 min-w-0 truncate">{o.name}</span>
                  <span className="text-sm tabular-nums shrink-0">
                    {formatBRL(o.price)}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="flex items-end gap-1.5 pt-1">
          <div className="flex-1 min-w-0">
            <Label className="text-[10px] uppercase text-muted-foreground">
              Nova oferta
            </Label>
            <Input
              placeholder="Ex: Presencial"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="h-8 text-sm mt-0.5"
            />
          </div>
          <div className="w-32 shrink-0">
            <Label className="text-[10px] uppercase text-muted-foreground">Preço</Label>
            <div className="relative mt-0.5">
              <Banknote className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                className="h-8 pl-7 text-sm tabular-nums"
              />
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-8 shrink-0"
            disabled={!newName.trim() || create.isPending}
            onClick={() =>
              create.mutate({
                productId,
                name: newName.trim(),
                // Vazio ou inválido vira "0" — oferta sem preço é um preço
                // zero explícito, não um NUMERIC quebrado.
                price: normalizePrice(newPrice) ?? "0",
              })
            }
          >
            {create.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
